import type { Cache, Resolver, Variables } from '@urql/exchange-graphcache';
import { stringifyVariables } from 'urql';
import isArgsSimilar from '@/client/exchanges/cacheExchange/isArgsSimilar';
import sortByCursor, {
  compareLinksByOrderBy,
  isOrderByArg,
  type OrderByArg,
} from '@/client/exchanges/cacheExchange/sortByCursor';

interface PaginationParams {
  cursorArgument?: string;
  cursorExclusivePredicateArgument?: string;
  [key: string]: unknown;
}

type CursorWindow = {
  lower: number | null;
  upper: number | null;
};

type CandidateField = {
  fieldKey: string;
  links: string[];
  window: CursorWindow | null;
};

type CacheEntry = {
  linksFingerprint: string;
  result: string[];
};

const resolverCacheByStore = new WeakMap<Cache, Map<string, CacheEntry>>();

const getResolverCache = (cache: Cache) => {
  const existingCache = resolverCacheByStore.get(cache);
  if (existingCache) {
    return existingCache;
  }

  const resolverCache = new Map<string, CacheEntry>();
  resolverCacheByStore.set(cache, resolverCache);
  return resolverCache;
};

const buildLinksFingerprint = (
  candidates: CandidateField[],
  cache: Cache,
  cursorArgument: string,
): string => {
  let fingerprint = '';

  for (const candidate of candidates) {
    fingerprint += candidate.fieldKey;
    fingerprint += ':';
    fingerprint += candidate.links
      .map((link) => {
        const cursorValue = cache.resolve(link, cursorArgument);
        return `${link}:${typeof cursorValue === 'string' ? cursorValue : ''}`;
      })
      .join(',');
    fingerprint += '|';
  }

  return fingerprint;
};

const getComparableTimestamp = (value: unknown): number | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const unixTimestamp = Date.parse(value);
  return Number.isNaN(unixTimestamp) ? null : unixTimestamp;
};

const getCursorTimestamp = (
  cache: Cache,
  entityKey: string,
  cursorArgument: string,
) => getComparableTimestamp(cache.resolve(entityKey, cursorArgument));

const getCursorWindow = (
  args: Variables,
  cursorArgument: string,
): CursorWindow | null => {
  const whereArgs = args?.where as Record<string, unknown> | undefined;
  const cursorArgs = whereArgs?.[cursorArgument] as
    | Record<string, unknown>
    | undefined;

  if (!cursorArgs || typeof cursorArgs !== 'object') {
    return null;
  }

  const lowerGt = getComparableTimestamp(cursorArgs._gt);
  const lowerGte = getComparableTimestamp(cursorArgs._gte);
  const upperLt = getComparableTimestamp(cursorArgs._lt);
  const upperLte = getComparableTimestamp(cursorArgs._lte);

  const lowerCandidates = [lowerGt, lowerGte].filter(
    (value): value is number => value !== null,
  );
  const upperCandidates = [upperLt, upperLte].filter(
    (value): value is number => value !== null,
  );

  return {
    lower: lowerCandidates.length > 0 ? Math.max(...lowerCandidates) : null,
    upper: upperCandidates.length > 0 ? Math.min(...upperCandidates) : null,
  };
};

const windowsOverlap = (
  firstWindow: CursorWindow | null,
  secondWindow: CursorWindow | null,
) => {
  if (!firstWindow || !secondWindow) {
    return true;
  }

  const lowerBoundary = Math.max(
    firstWindow.lower ?? Number.NEGATIVE_INFINITY,
    secondWindow.lower ?? Number.NEGATIVE_INFINITY,
  );
  const upperBoundary = Math.min(
    firstWindow.upper ?? Number.POSITIVE_INFINITY,
    secondWindow.upper ?? Number.POSITIVE_INFINITY,
  );

  return lowerBoundary <= upperBoundary;
};

const isOrderedByRequestedOrder = (
  links: string[],
  cache: Cache,
  orderBy: OrderByArg,
  cursorArgument: string,
) => {
  if (links.length < 2) {
    return true;
  }

  for (let index = 1; index < links.length; index += 1) {
    if (
      compareLinksByOrderBy(
        links[index - 1],
        links[index],
        cache,
        orderBy,
        cursorArgument,
      ) > 0
    ) {
      return false;
    }
  }

  return true;
};

const cursorPagination =
  ({
    cursorArgument = 'created_at',
    cursorExclusivePredicateArgument,
  }: PaginationParams = {}): Resolver =>
  (_parent, fieldArgs, cache, info) => {
    const { parentKey: entityKey, fieldName } = info;
    const orderBy = fieldArgs.order_by;

    if (!isOrderByArg(orderBy)) {
      return undefined;
    }

    const allFields = cache.inspectFields(entityKey);

    const fieldInfos = allFields.filter(
      (fieldInfo) => fieldInfo.fieldName === fieldName,
    );
    const fieldArgsSignature = stringifyVariables(fieldArgs);
    const exactFieldInfo = fieldInfos.find((fieldInfo) => {
      if (fieldInfo.arguments === null) {
        return false;
      }

      return stringifyVariables(fieldInfo.arguments) === fieldArgsSignature;
    });
    const hasExactFieldArgs = !!exactFieldInfo;

    if (fieldInfos.length === 0) {
      return undefined;
    }

    const requestWindow = getCursorWindow(fieldArgs, cursorArgument);
    const candidates: CandidateField[] = [];

    fieldInfos.forEach(({ fieldKey, arguments: args }) => {
      if (
        args === null ||
        !isArgsSimilar(
          fieldArgs,
          args,
          cursorArgument,
          cursorExclusivePredicateArgument,
        )
      ) {
        return;
      }

      const resolvedLinks = cache.resolve(entityKey, fieldKey);
      const links = Array.isArray(resolvedLinks)
        ? resolvedLinks.filter(
            (link): link is string =>
              typeof link === 'string' && link.length > 0,
          )
        : [];

      if (links.length === 0) {
        return;
      }

      candidates.push({
        fieldKey,
        links,
        window: getCursorWindow(args, cursorArgument),
      });
    });

    const selectedCandidates: CandidateField[] = [];
    const selectedCandidateSet = new Set<CandidateField>();
    let didSelectCandidate = true;

    while (didSelectCandidate) {
      didSelectCandidate = false;

      candidates.forEach((candidate) => {
        if (selectedCandidateSet.has(candidate)) {
          return;
        }

        const overlapsRequest = windowsOverlap(requestWindow, candidate.window);
        const overlapsSelected = selectedCandidates.some((selectedCandidate) =>
          windowsOverlap(selectedCandidate.window, candidate.window),
        );

        if (!selectedCandidates.length ? overlapsRequest : overlapsSelected) {
          selectedCandidates.push(candidate);
          selectedCandidateSet.add(candidate);
          didSelectCandidate = true;
        }
      });
    }

    const resolvedCurrentPage = exactFieldInfo
      ? cache.resolve(entityKey, fieldName, fieldArgs)
      : null;
    const hasCurrentPage =
      resolvedCurrentPage !== null && resolvedCurrentPage !== undefined;
    const currentPageLinks = Array.isArray(resolvedCurrentPage)
      ? resolvedCurrentPage.filter(
          (link): link is string => typeof link === 'string' && link.length > 0,
        )
      : [];

    const cacheKey = `${entityKey}.${fieldName}:${fieldArgsSignature}`;
    const fingerprint = buildLinksFingerprint(
      selectedCandidates,
      cache,
      cursorArgument,
    );
    const resolverCache = getResolverCache(cache);
    const cached = resolverCache.get(cacheKey);

    if (hasCurrentPage && cached && cached.linksFingerprint === fingerprint) {
      return cached.result;
    }

    const visited = new Set();
    let result: string[] = [];

    selectedCandidates.forEach((candidate) => {
      const tempResult: string[] = [];

      candidate.links.every((link) => {
        const cursorValue = cache.resolve(link, cursorArgument);
        if (
          cursorValue === undefined ||
          (!cursorExclusivePredicateArgument && cursorValue === null)
        ) {
          return true;
        }

        if (visited.has(link)) {
          return true;
        }

        tempResult.push(link);
        visited.add(link);
        return true;
      });

      result = [...result, ...tempResult];
    });

    if (result.length > 1) {
      const alreadySorted = isOrderedByRequestedOrder(
        result,
        cache,
        orderBy,
        cursorArgument,
      );

      if (!alreadySorted) {
        result = sortByCursor(result, cache, cursorArgument, orderBy);
      }
    }

    if (!cursorExclusivePredicateArgument && currentPageLinks.length > 0) {
      const currentPageSet = new Set(currentPageLinks);
      const currentPageTimestamps: number[] = [];
      for (const link of currentPageLinks) {
        const timestamp = getCursorTimestamp(cache, link, cursorArgument);
        if (timestamp !== null) {
          currentPageTimestamps.push(timestamp);
        }
      }

      if (currentPageTimestamps.length > 0) {
        const currentPageLower = Math.min(...currentPageTimestamps);
        const currentPageUpper = Math.max(...currentPageTimestamps);

        result = result.filter((link) => {
          if (currentPageSet.has(link)) {
            return true;
          }

          const linkTimestamp = getCursorTimestamp(cache, link, cursorArgument);
          if (linkTimestamp === null) {
            return false;
          }

          const isInsideCurrentPageWindow =
            linkTimestamp >= currentPageLower &&
            linkTimestamp <= currentPageUpper;

          return !isInsideCurrentPageWindow;
        });
      }
    }

    if (hasCurrentPage) {
      resolverCache.set(cacheKey, { linksFingerprint: fingerprint, result });
    }

    if (!hasExactFieldArgs || !hasCurrentPage) {
      return undefined;
    }

    return result;
  };

export default cursorPagination;
